import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { FileText, FolderTree, MessageSquare, RefreshCw, Upload, BarChart3, Building2 } from 'lucide-react'
import {
  PageHeader, Card, CardBody, Button, LoadingSpinner, ErrorMessage, FormSelect
} from '../components/common'
import api from '../services/api'
import { knowledgeBaseApi } from '../services/api'
import { extractErrorMessage } from '../utils/errorUtils'

const CATEGORIES = [
  'RulesAndRegulations', 'StrataDocuments', 'Financial', 'Insurance',
  'MeetingMinutes', 'Maintenance', 'General'
]

const categoryLabels = {
  RulesAndRegulations: 'Rules & Regulations',
  StrataDocuments: 'Strata Documents',
  Financial: 'Financial',
  Insurance: 'Insurance',
  MeetingMinutes: 'Meeting Minutes',
  Maintenance: 'Maintenance',
  General: 'General'
}

export default function KnowledgeBase() {
  const navigate = useNavigate()
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [buildings, setBuildings] = useState([])
  const [loadingBuildings, setLoadingBuildings] = useState(true)

  const [buildingId, setBuildingId] = useState(
    () => localStorage.getItem('selectedBuildingId') || ''
  )

  useEffect(() => {
    loadBuildings()
  }, [])

  useEffect(() => {
    if (buildingId) {
      localStorage.setItem('selectedBuildingId', buildingId)
      loadStats()
    } else {
      setLoading(false)
      setStats(null)
    }
  }, [buildingId])

  const loadBuildings = async () => {
    try {
      setLoadingBuildings(true)
      const response = await api.get('/Building', { params: { pageNumber: 1, pageSize: 200 } })
      const items = response.data.items || []
      setBuildings(items)
      // Auto-select first building if none selected
      if (!buildingId && items.length > 0) {
        setBuildingId(items[0].id)
      }
    } catch (err) {
      console.error('Error loading buildings:', err)
    } finally {
      setLoadingBuildings(false)
    }
  }

  const loadStats = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await knowledgeBaseApi.getStats(buildingId)
      setStats(response.data)
    } catch (err) {
      setError(extractErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  const handleBuildingChange = (e) => {
    setBuildingId(e.target.value)
  }

  const buildingOptions = [
    { value: '', label: 'Select a building...' },
    ...buildings.map(b => ({ value: b.id, label: b.name }))
  ]

  return (
    <div className="space-y-6">
      <PageHeader title="Knowledge Base" subtitle="Upload documents, sync with Bedrock, and ask questions" />

      {/* Building Selector */}
      <Card>
        <CardBody>
          <div className="flex items-center gap-4">
            <Building2 size={20} className="text-slate-400 flex-shrink-0" />
            <div className="flex-1 max-w-sm">
              {loadingBuildings ? (
                <div className="text-sm text-slate-500">Loading buildings...</div>
              ) : (
                <FormSelect
                  label=""
                  value={buildingId}
                  onChange={handleBuildingChange}
                  options={buildingOptions}
                />
              )}
            </div>
            {buildingId && (
              <span className="text-xs text-slate-400">
                Building context is shared across all Knowledge Base pages
              </span>
            )}
          </div>
        </CardBody>
      </Card>

      {error && <ErrorMessage message={error} onDismiss={() => setError(null)} />}

      {!buildingId && (
        <Card>
          <CardBody>
            <p className="text-slate-500 text-center py-8">Please select a building above to get started.</p>
          </CardBody>
        </Card>
      )}

      {buildingId && loading && <LoadingSpinner />}

      {buildingId && !loading && (
        <>
          {/* Quick Actions */}
          <div className="flex flex-wrap gap-3">
            <Button variant="primary" icon={<Upload size={16} />} onClick={() => navigate('/knowledge-base/upload')}>Upload Document</Button>
            <Button variant="secondary" icon={<MessageSquare size={16} />} onClick={() => navigate('/knowledge-base/chat')}>Ask a Question</Button>
            <Button variant="secondary" icon={<RefreshCw size={16} />} onClick={() => navigate('/knowledge-base/sync')}>Sync Status</Button>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'Total Documents', value: stats?.totalDocuments ?? 0, icon: FileText, color: 'text-primary-600', bg: 'bg-primary-50', href: '/knowledge-base/documents' },
              { label: 'Indexed', value: stats?.indexedDocuments ?? 0, icon: BarChart3, color: 'text-green-600', bg: 'bg-green-50', href: '/knowledge-base/sync' },
              { label: 'Topics', value: stats?.totalTopics ?? 0, icon: FolderTree, color: 'text-blue-600', bg: 'bg-blue-50', href: '/knowledge-base/topics' },
              { label: 'Chat Sessions', value: stats?.totalSessions ?? 0, icon: MessageSquare, color: 'text-purple-600', bg: 'bg-purple-50', href: '/knowledge-base/chat' },
            ].map(card => (
              <Card key={card.label} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate(card.href)}>
                <CardBody className="flex items-center gap-4">
                  <div className={`p-3 rounded-xl ${card.bg}`}>
                    <card.icon size={24} className={card.color} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{card.value}</p>
                    <p className="text-sm text-slate-500">{card.label}</p>
                  </div>
                </CardBody>
              </Card>
            ))}
          </div>

          {/* Category Breakdown */}
          {stats?.byCategory && Object.keys(stats.byCategory).length > 0 && (
            <Card>
              <CardBody>
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Documents by Category</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {CATEGORIES.map(cat => (
                    <div key={cat} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="text-sm text-slate-700">{categoryLabels[cat]}</span>
                      <span className="text-sm font-semibold text-slate-900">{stats.byCategory[cat] || 0}</span>
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          )}

          {/* Status Summary */}
          {stats && (stats.failedDocuments > 0 || stats.processingDocuments > 0) && (
            <Card>
              <CardBody>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Attention Needed</h3>
                <div className="flex gap-4">
                  {stats.processingDocuments > 0 && (
                    <div className="flex items-center gap-2 text-amber-600">
                      <RefreshCw size={16} className="animate-spin" />
                      <span className="text-sm">{stats.processingDocuments} document(s) processing</span>
                    </div>
                  )}
                  {stats.failedDocuments > 0 && (
                    <div className="flex items-center gap-2 text-red-600">
                      <span className="text-sm font-medium">{stats.failedDocuments} document(s) failed</span>
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>
          )}
        </>
      )}
    </div>
  )
}
